import React, { useRef, useState, useEffect } from "react";
import {
  useNavigate,
  useLocation
} from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { UploadCloud, FileText, ArrowRight, ArrowLeft, Check,} from "lucide-react";
import "../index.css";

export default function SignDocument() {
  const navigate = useNavigate();
  const location = useLocation();
  const documentId =
  new URLSearchParams(
    location.search
  ).get("docId");
  // Set when the user came from a request's details page (Sign Document button)
  const workflowRequestId = new URLSearchParams(location.search).get("workflowRequest");
  const [currentStep] = useState(0);
  const [recentDocs, setRecentDocs] = useState([]);
  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user")) || {};

  // ---- SECURE VAULT PICKER STATE ----
  const [showVault, setShowVault] = useState(false);
  const [vaultFiles, setVaultFiles] = useState([]);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [vaultSearch, setVaultSearch] = useState("");
  const [vaultPicking, setVaultPicking] = useState(null); // id of vault file being fetched

  useEffect(() => {
  if (documentId) {
    loadDocument();
  }
}, [documentId]);

  useEffect(() => {
    if (workflowRequestId) {
      // keep the request id safe across the whole signing wizard
      sessionStorage.setItem("workflowRequestId", workflowRequestId);
      loadWorkflowDocument();
    } else {
      sessionStorage.removeItem("workflowRequestId");
    }
  }, [workflowRequestId]);

  // Auto-select the document that belongs to the request being signed.
  // The file is tagged with workflowRequestId so review.js knows which request to
  // mark as signed once the user finishes the whole wizard.
  const loadWorkflowDocument = async () => {
    try {
      const email = (user.email || "").toLowerCase();
      const response = await axios.get(
        `${API_URL}/api/workflow/requests/${encodeURIComponent(workflowRequestId)}/file`,
        { params: { user_email: email }, responseType: "blob" }
      );

      let fileName = "Document.pdf";
      const disposition = response.headers["content-disposition"];
      if (disposition) {
        const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        if (match) fileName = decodeURIComponent(match[1]);
      }

      const file = new File([response.data], fileName, {
        type: response.data.type || "application/pdf",
      });

      setRecentDocs([
        {
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(2) + " MB",
          date: new Date().toLocaleDateString(),
          url: URL.createObjectURL(file),
          file,
          workflowRequestId,
        },
      ]);
    } catch (err) {
      console.error("Failed to load request document", err);
      alert("Could not load the document for this request.");
    }
  };

const loadDocument = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/api/get-document/${documentId}`,
      {
        responseType: "blob",
      }
    );

    const disposition = response.headers["content-disposition"];

let fileName = "Document.pdf";

if (disposition) {
  const match = disposition.match(/filename="?(.+?)"?$/);
  if (match) {
    fileName = match[1];
  }
}

const file = new File([response.data], fileName, {
  type: response.data.type || "application/pdf",
});

    const autoDoc = {
      name: file.name,
      size:
        (file.size / 1024 / 1024).toFixed(2) +
        " MB",
      date: new Date().toLocaleDateString(),
      url: URL.createObjectURL(file),
      file,
    };

    setRecentDocs([autoDoc]);

  } catch (err) {
    console.error("Failed to load document", err);
  }
};

  // ---- SECURE VAULT HELPERS ----
  const formatBytes = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const openVault = async () => {
    setShowVault(true);
    setVaultSearch("");
    setVaultLoading(true);
    try {
      const ownerEmail = localStorage.getItem("email") || user.email || "";
      const res = await axios.get(`${API_URL}/api/secure-drive/files`, {
        params: { owner_email: ownerEmail },
      });
      setVaultFiles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load vault files", err);
      alert("Could not load files from Secure Vault.");
    } finally {
      setVaultLoading(false);
    }
  };

  const handleSelectVaultFile = async (vf) => {
    // don't add the same vault file twice
    if (recentDocs.some((d) => d.vaultFileId === vf.id)) {
      setShowVault(false);
      return;
    }
    setVaultPicking(vf.id);
    try {
      // backend checks permission, gets the locked file from IPFS and unlocks it
      const res = await axios.get(
        `${API_URL}/api/secure-drive/download/${vf.id}`,
        {
          responseType: "blob",
          params: { owner_email: localStorage.getItem("email") || user.email || "", purpose: "sign" },
        }
      );
      const file = new File([res.data], vf.file_name, {
        type: vf.file_type || res.data.type || "application/pdf",
      });
      setRecentDocs((prev) => [
        {
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(2) + " MB",
          date: new Date().toLocaleDateString(),
          url: URL.createObjectURL(file),
          file,
          vaultFileId: vf.id, // link back to the vault file
          vaultCid: vf.cid,
        },
        ...prev,
      ]);
      setShowVault(false);
    } catch (err) {
      console.error("Failed to fetch vault file", err);
      alert("Could not load this file from Secure Vault.");
    } finally {
      setVaultPicking(null);
    }
  };

  const filteredVaultFiles = vaultFiles.filter((f) =>
    (f.file_name || "").toLowerCase().includes(vaultSearch.toLowerCase())
  );

  const steps = ["Upload", "Select Identity", "Choose Mode", "Advanced Options", "Review & Sign"];

  const handleNext = () => {
    if (recentDocs.length === 0) {
      alert("Please upload at least one document before proceeding.");
      return;
    }
    navigate("/signdoc", {
  state: {
    uploadedFiles: recentDocs,
    documentId: documentId
  }
});
  };

  const handleCancel = () => {
    if (workflowRequestId) {
      navigate(`/request-details/${workflowRequestId}`);
      return;
    }
    setRecentDocs([]);
    alert("Signing process cancelled");
  };

  // OPEN FILE PICKER
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  // HANDLE FILE UPLOAD
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);

    const uploadedFiles = files.map((file) => ({
  name: file.name,
  size:
    (
      file.size /
      1024 /
      1024
    ).toFixed(2) + " MB",
  date:
    new Date().toLocaleDateString(),
  url:
    URL.createObjectURL(file),

  file, // <-- important
}));

    // NEW FILES ADD TO TOP
    setRecentDocs((prev) => [...uploadedFiles, ...prev]);
  };

  return (
    <div className="w-full bg-white mx-auto">

      {/* STEPS */}
      <div className="flex items-center justify-between mb-2 h-[72px]">
        {steps.map((step, index) => {
          const isDone = index < currentStep;
          const isCurrent = index === currentStep;

          let circleClass = "bg-white border-gray-300 text-gray-500";
          let labelClass = "text-gray-500";
          if (isDone) {
            circleClass = "bg-green-500 border-green-500 text-white";
            labelClass = "text-green-600";
          } else if (isCurrent) {
            circleClass = "bg-blue-600 border-blue-600 text-white";
            labelClass = "text-blue-600";
          }

          return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== steps.length - 1 && (
              <div className={`absolute top-5 left-1/2 w-full h-[2px] ${index < currentStep ? "bg-green-500" : "bg-gray-300"}`} />
            )}
            <div className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${circleClass}`}>
              {index < currentStep ? <Check size={12} /> : index + 1}
            </div>
            <p className={`mt-1 text-sm font-medium text-center hidden sm:block ${labelClass}`}>{step}</p>
          </div>
          );
        })}
      </div>

      {/* UPLOAD BOX */}
      <div className="border-2 border-dashed border-blue-300 rounded-2xl py-4 px-3 sm:py-6 sm:px-6 w-full max-w-2xl mx-auto text-center hover:border-blue-600 hover:bg-blue-50 transition">
              
                <div className="flex justify-center mb-2">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-blue-100 flex items-center justify-center shadow-md mb-2">
                    <UploadCloud className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-blue-600 mb-2" />
                  </div>
                </div>
      
                <p
                  style={{
                  fontSize: "16px", // changed from text-2xl → 16px
                  fontWeight: 500,  // semibold
                  color: "#374151", // text-gray-700
                  }}>
                  Drag & Drop your document here
                </p>

      
                <p className="text-base font-normal text-gray-400 mt-2 mb-2">or</p>
      
                {/* HIDDEN FILE INPUT */}
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileUpload}
                />
      
                {/* BROWSE + VAULT BUTTONS */}
                <div className="flex flex-wrap items-center justify-center gap-3 mt-3">
                  <button
                    onClick={handleBrowseClick}
                    className="inline-flex items-center justify-center h-[44px] px-6 bg-[#2563EB] text-white text-sm font-medium rounded-[10px] hover:bg-[#1D4ED8] transition"
                  >
                    Browse Files
                  </button>
                  <button
                    onClick={openVault}
                    className="inline-flex items-center justify-center h-[44px] px-6 bg-white text-[#2563EB] border border-[#2563EB] text-sm font-medium rounded-[10px] hover:bg-blue-50 transition"
                  >
                    Select from Secure Vault
                  </button>
                </div>
      
                <p className="text-sm font-normal text-gray-400 mt-4">
                  Supported formats: PDF, DOCX, TXT, XLSX, PNG, JPG
                  <br />
                  Maximum file size: 100 MB
                </p>
              </div>



      {/* RECENT FILES */}
            <div className="sm:10 ">
      
                <div className="flex items-center justify-between ">
                  <h3 className="text-base sm:text-14px md:text-xl font-semibold text-gray-700">
                    Recent Documents 
                  </h3>
      
                  <span className="text-sm text-gray-400">
                    {recentDocs.length} Files
                  </span>
                </div> 
      
                {/* EMPTY STATE */}
                {recentDocs.length === 0 && (
                  <div className="border rounded-2xl  text-center text-gray-400 bg-gray-50">
                    No files uploaded yet
                  </div>
                )}
      
                {/* FILE LIST */}
                <div className="space-y-4">
                  {recentDocs.map((doc) => (
                    <div
                      key={doc.url}
                      className="flex items-center justify-between border border-gray-200 rounded-xl px-3 py-2 hover:shadow-md hover:border-blue-300 transition">
                      <div className="flex items-center gap-4">
      
                        <div className="bg-red-100 p-2 rounded-lg">
        <FileText className="text-red-500 w-4 h-4" />
      </div>
                        <div className="flex flex-col">
        <p className="text-10px text-[#111827] break-all">
          {doc.name}
          {doc.vaultFileId && (
            <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700 font-medium">
              Secure Vault
            </span>
          )}
        </p>
      
        <p className="text-xs text-[#4B5563]">
          {doc.size} • {doc.date}
        </p>
      </div>
                      </div>
      
                      {/* OPEN FILE */}
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition text-xs font-medium"
                      >
                        View
                      </a>
                    </div>
                  ))}
                </div>

        {/* VIEW ALL */}
        <div
          onClick={() => {
            if (recentDocs.length === 0) { alert("No documents uploaded yet"); return; }
            const allDocs = recentDocs.map((doc, index) => `${index + 1}. ${doc.name} (${doc.size})`).join("\n");
            alert(`Uploaded Documents:\n\n${allDocs}`);
          }}
          className="text-blue-600 text-14px font-semibold mt-1 cursor-pointer hover:underline"
        >
          View all documents →
        </div>
            </div>

      {/* CANCEL / NEXT */}
      <div className="flex justify-between mt-3">
        <button onClick={handleCancel} className="inline-flex items-center gap-1.5 h-[44px] px-6 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium">
          <ArrowLeft size={14} /> Cancel
        </button>
        <button onClick={handleNext} className="flex items-center gap-1.5 h-[44px] px-6 py-4 rounded-[10px] text-white text-sm font-medium bg-[#2563EB] hover:bg-[#1D4ED8] transition">
          Next <ArrowRight size={14} />
        </button>
      </div>

      {/* SECURE VAULT MODAL */}
      {showVault && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowVault(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-800">Select from Secure Vault</h3>
              <button
                onClick={() => setShowVault(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-5 pt-4">
              <input
                type="text"
                value={vaultSearch}
                onChange={(e) => setVaultSearch(e.target.value)}
                placeholder="Search files..."
                className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
              />
            </div>

            <div className="p-5 overflow-y-auto space-y-3">
              {vaultLoading && (
                <p className="text-center text-sm text-gray-400">Loading files...</p>
              )}

              {!vaultLoading && filteredVaultFiles.length === 0 && (
                <p className="text-center text-sm text-gray-400">No files found in your vault.</p>
              )}

              {filteredVaultFiles.map((f) => (
                <div
                  key={f.id}
                  className="flex items-center justify-between border border-gray-200 rounded-xl px-3 py-2 hover:border-blue-300 hover:shadow-sm transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="bg-red-100 p-2 rounded-lg shrink-0">
                      <FileText className="text-red-500 w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-[#111827] truncate">{f.file_name}</p>
                      <p className="text-xs text-[#4B5563]">
                        {formatBytes(f.file_size || 0)} • {new Date(f.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSelectVaultFile(f)}
                    disabled={vaultPicking === f.id}
                    className="px-3 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition text-xs font-medium disabled:opacity-50"
                  >
                    {vaultPicking === f.id ? "Loading..." : "Select"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  
  );
}